import { useRef, useEffect, useContext, useMemo, useCallback } from 'react';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { CollabEditPlugin } from '@atlaskit/editor-plugin-collab-edit';

import {
	generateSlug,
	buildContentPath,
	RoutesContext,
	useNavigationPolicy,
} from '@confluence/route-manager';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import { COMPANY_HUB } from '@confluence/named-routes';
import type { NativeCollabInjectedProps } from '@confluence/native-collab';
import type { FlagsStateContainer } from '@confluence/flags';
import type { DialogsStateContainer } from '@confluence/dialogs';
import { useForceRefetchBreadcrumbs } from '@confluence/breadcrumbs-utils/entry-points/useForceRefetchBreadcrumbs';

import { pagePublishedState, getClosePreloadUrl } from '../utils';

import { useEditorBreadcrumbs } from './useEditorBreadcrumbs';
import type { UseDraftSyncProps } from './useDraftSync/useDraftSync';
import { useDraftSync } from './useDraftSync/useDraftSync';
import type { UsePublishProps } from './usePublish/usePublish';
import { usePublish } from './usePublish/usePublish';
import { useForceRefetchContent } from './useForceRefetchContent';

type UseDraftSyncAndPublishProps = Omit<UseDraftSyncProps, 'isPublishingContent'> &
	Omit<UsePublishProps, 'isSavingDraft' | 'afterPublish' | 'saveDraftPromise'> &
	Pick<NativeCollabInjectedProps, 'title' | 'viewMode'> & {
		onCollabError: () => void;
		isUnpublishedDraft: boolean;
		flags?: FlagsStateContainer;
		dialogs?: DialogsStateContainer;
		editorPresetAPI?: PublicPluginAPI<[CollabEditPlugin]> | null;
		onPublishUpdateTitleInPageTree?: () => void;
	};

export enum ContentTypeType {
	PAGE = 'page',
	BLOGPOST = 'blogpost',
}

export const useDraftSyncAndPublish = ({
	contentId,
	contentType,
	refetchNativeCollab,
	editorActions,
	embeddedConfluenceSource,
	isFirstParticipant,
	isUnpublishedDraft,
	spaceKey,
	title,
	version,
	onCollabError,
	checkIfConditionPreventingPublish,
	flags,
	dialogs,
	editorPresetAPI,
	editorSessionId,
	onPublishUpdateTitleInPageTree,
	startingLinks,
	viewMode,
}: UseDraftSyncAndPublishProps) => {
	const _isSavingDraft = useRef<boolean>(false);
	const { push, match, matchRoute } = useContext(RoutesContext);
	const { routePolicy } = useNavigationPolicy();
	const forceRefetchContent = useForceRefetchContent(contentId, spaceKey);
	const forceRefetchBreadcrumbs = useForceRefetchBreadcrumbs(contentId, spaceKey, match?.name);
	const pushWithRoutePolicy = useCallback(
		(url) => {
			const targetUrl = isCompanyHubSpaceKey(spaceKey) ? COMPANY_HUB.toUrl() : url;
			const route = matchRoute(targetUrl);

			if (
				routePolicy &&
				!routePolicy.willPushInSelf(match?.name, contentId, route?.name, route?.params?.contentId)
			) {
				return;
			}

			push(targetUrl);
		},
		[matchRoute, routePolicy, contentId, match, push, spaceKey],
	);

	const breadcrumbs = useEditorBreadcrumbs(contentId);

	const navigateOnClose = useCallback(
		async (args?: { isDiscarding?: boolean }) => {
			// Refetch content to resolve stale cache issues
			await forceRefetchContent();

			// The `isDiscarding` check here prevents a race condition with the page tree refresh code
			// triggered when discarding a blank draft (in EditorPrimaryToolbarComponent.tsx).
			if (isUnpublishedDraft && !args?.isDiscarding) {
				onPublishUpdateTitleInPageTree?.();
			}

			const url = getClosePreloadUrl({
				contentId,
				spaceKey,
				contentType,
				breadcrumbs,
				isUnpublishedDraft,
			});

			pushWithRoutePolicy(url);
		},
		[
			isUnpublishedDraft,
			contentType,
			breadcrumbs,
			spaceKey,
			contentId,
			forceRefetchContent,
			pushWithRoutePolicy,
			onPublishUpdateTitleInPageTree,
		],
	);

	const navigateOnPublish = (res, newSpaceKey) => {
		void forceRefetchBreadcrumbs();

		const url = buildContentPath(
			newSpaceKey || spaceKey,
			contentId,
			contentType,
			res.createdDate,
			generateSlug(title),
		);
		pagePublishedState.pageJustPublished = true;

		pushWithRoutePolicy(url);
	};

	// Draft Sync Handling
	const {
		triggerSaveDraft,
		isSavingDraft,
		saveDraft,
		saveDraftPromise,
		cancelDebouncedSaveDraft,
		saveDraftError,
	} = useDraftSync({
		contentId,
		editorActions,
		contentType,
		isFirstParticipant,
		onCollabError,
		editorPresetAPI,
		editorSessionId,
		viewMode,
	});
	useEffect(() => {
		_isSavingDraft.current = isSavingDraft;
	}, [isSavingDraft]);

	// Publish Handling
	const { isPublishingContent, onPublish } = usePublish({
		contentId,
		contentType,
		embeddedConfluenceSource,
		refetchNativeCollab,
		editorActions,
		spaceKey,
		afterPublish: (res, newSpaceKey) => {
			cancelDebouncedSaveDraft();
			onPublishUpdateTitleInPageTree?.();
			navigateOnPublish(res, newSpaceKey);
		},
		version,
		onCollabError,
		saveDraftPromise,
		checkIfConditionPreventingPublish,
		flags,
		dialogs,
		startingLinks,
	});

	return useMemo(
		() => ({
			triggerSaveDraft,
			isSavingDraft,
			saveDraftError,
			isPublishingContent,
			cancelDebouncedSaveDraft,
			onPublish,
			navigateOnClose,
			saveDraft,
		}),
		[
			triggerSaveDraft,
			isSavingDraft,
			saveDraftError,
			isPublishingContent,
			cancelDebouncedSaveDraft,
			onPublish,
			navigateOnClose,
			saveDraft,
		],
	);
};
